import React, { useMemo, useState, useEffect, CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useJetpackConfigSelector } from "../../../redux/jetpackConfig";
import KC_Button, {
  KC_ButtonSize
} from "../../../designsystem/KC_Button/KC_Button";
import KC_Separator from "../../../designsystem/KC_Separator/KC_Separator";
import { KC_Sizes } from "../../../designsystem/ds_commons";
import KC_Heading3 from "../../../designsystem/typography/KC-Heading/KC_Heading3";
import ColorPalettePicker from "../../ColorPalettePicker/ColorPalettePicker";
import KC_Icon_Button from "../../../designsystem/KC_Button/KC_Icon_Button";
import {
  faEyeDropper,
  faFillDrip,
  faTimes
} from "@fortawesome/free-solid-svg-icons";
import TestsID from "../../../utils/TestsID";
import ColorInput from "../../ColorInput/ColorInput";
import { ColorPicker, type Color, useColor } from "react-color-palette";
import { useDispatch } from "react-redux";
import { setColorKey } from "../../../redux/controller/controller";
import { tt } from "../../../utils/i18n/i18n";

import {
  COLOR_PICKER_MODEL,
  COLOR_PICKER_WIDTH
} from "../../../utils/constants";
import { hexToRgb } from "../../../utils/helpers/hexToRgb";
import { rgbToHsv } from "../../../utils/helpers/rgbToHsv";

interface ColorSelectionRowProps {
  color: string;
  onColorChanged: (colorKey: string, hexColor: string) => void;
  colorKey: string;
  label: string;
  isPOSA?: boolean;
}

const ColorSelectionRow = (props: ColorSelectionRowProps) => {
  const dispatch = useDispatch();
  const jetpackConfig = useJetpackConfigSelector();
  const [colorTouched, setColorTouched] = useState<boolean>(false);

  const colorObject = useMemo<Color>(() => {
    const rgb = hexToRgb(props.color);
    return {
      hex: props.color,
      rgb,
      hsv: rgbToHsv(rgb)
    };
  }, [props.color]);

  const [color, setColor] = useColor(COLOR_PICKER_MODEL, colorObject.hex);

  const handleColorChange = (newColor: Color) => {
    setColor(newColor);
  };

  const isShowLogoColorPicker = useMemo(() => {
    return !!jetpackConfig?.logo_url;
  }, [jetpackConfig]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && colorTouched) {
        setColorTouched(false);
      }
    };

    if (colorTouched) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [colorTouched]);

  const ColorPickerModal = () => {
    if (!colorTouched) {
      return null;
    }

    return createPortal(
      <>
        <div className="position-fixed" style={COLOR_PICKER_STYLE} />
        <div className="position-fixed" style={COLOR_PICKER_INNER_STYLE}>
          <div className="d-flex gap-sm mb-3">
            <KC_Button
              text={tt("select_color")}
              size="lg"
              type="button"
              className="flex-grow-1"
              onClick={() => {
                void props.onColorChanged(props.colorKey, color.hex);
                setColorTouched(false);
              }}
            />
            <KC_Button
              size={KC_ButtonSize.Large}
              isPrimary={false}
              isPrimaryLight={false}
              isSecondary={false}
              isInverted={false}
              isWhite={true}
              showIconOnTheLeft={true}
              icon={faTimes}
              onClick={() => {
                setColorTouched(false);
              }}
              extraStyle={{
                width: "unset !important"
              }}
            />
          </div>
          <KC_Separator size={KC_Sizes.sm} />
          <ColorPicker
            width={COLOR_PICKER_WIDTH}
            height={COLOR_PICKER_WIDTH}
            color={color}
            onChange={handleColorChange}
            onChangeComplete={(color: Color) => {
              props.onColorChanged(props.colorKey, color.hex);
            }}
            hideHSV={true}
            dark={true}
          />
        </div>
      </>,
      document.body
    );
  };

  return (
    <div
      id={`color-selection-row-${props.colorKey}`}
      className="d-flex flex-row align-items-center justify-content-between py-2 px-0 w-100 st-gap saas-game-configuration__colors-selector-row border-top"
    >
      <div>
        <KC_Heading3 text={props.label} />
      </div>
      <div className="d-flex justify-content-center align-items-center">
        <ColorPickerModal />

        <div className="d-flex flex-row st-gap align-items-center">
          <div
            onClick={(e) => e.stopPropagation()}
            className="d-none d-xl-block"
          >
            <ColorPalettePicker
              isPOSA={props.isPOSA}
              colorKey={props.colorKey}
              propsSelectedColor={props.color}
              onColorSelect={(key, hex) => props.onColorChanged(key, hex)}
            />
          </div>

          <div className="d-flex gap-sm">
            <KC_Icon_Button
              size={KC_ButtonSize.Large}
              isInverted={true}
              icon={faFillDrip}
              onClick={() => {
                setColorTouched(true);
              }}
            />
          </div>

          {isShowLogoColorPicker && (
            <KC_Icon_Button
              size={KC_ButtonSize.Large}
              isInverted={true}
              onClick={() => {
                dispatch(setColorKey(props.colorKey));
              }}
              id={`${TestsID.SAAS_COLOR_SELECTOR}_${props.colorKey.toUpperCase()}`}
              icon={faEyeDropper}
            />
          )}

          <div
            className="d-none d-xl-block"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <ColorInput
              onChange={(hex) => props.onColorChanged(props.colorKey, hex)}
              initialValue={props.color}
            />
          </div>

          <div
            onClick={() => {
              setColorTouched(true);
            }}
            id={`${TestsID.SAAS_COLOR_SELECTOR}_${props.colorKey.toUpperCase()}`}
            className="color-square"
            style={{
              backgroundColor: props.color
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ColorSelectionRow;

const COLOR_PICKER_STYLE: CSSProperties = {
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  zIndex: 9998
};

const COLOR_PICKER_INNER_STYLE: CSSProperties = {
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  zIndex: 9999,
  backgroundColor: "#1e1e1e",
  borderRadius: "8px",
  padding: "20px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)"
};
